import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown, Pencil, Search, Trash2 } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Button, Select, cn } from './ui';

export interface Column<T> {
  key: string;
  header: string;
  /** Omit to make the column unsortable — actions and icon columns. */
  sort?: (a: T, b: T) => number;
  render: (row: T) => ReactNode;
  width?: string;
  align?: 'left' | 'right';
}

interface DataTableProps<T> {
  rows: readonly T[];
  columns: ReadonlyArray<Column<T>>;
  rowKey: (row: T) => string;
  /** Fields searched by the toolbar box. */
  search?: (row: T) => string;
  searchPlaceholder?: string;
  toolbar?: ReactNode;
  onEdit?: (row: T) => void;
  onDelete?: (rows: readonly T[]) => void;
  deleteLabel?: (rows: readonly T[]) => string;
  empty?: ReactNode;
  pageSize?: number;
}

const PAGE_SIZES = [10, 20, 50, 100] as const;

/**
 * The table every list screen is built from.
 *
 * Modelled on the pattern PBX admins already know: a toolbar of actions that act on the current
 * selection, a search box on the right, sortable headers, per-row edit and delete, and a footer
 * carrying the total. Sorting, filtering and paging are all client-side — a PBX has tens of
 * extensions and trunks, not thousands, so a round trip per keystroke would be slower and more
 * fragile than doing it here. Call history is the exception and keeps its own keyset pagination.
 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  search,
  searchPlaceholder = 'Search',
  toolbar,
  onEdit,
  onDelete,
  deleteLabel,
  empty,
  pageSize: initialPageSize = 20,
}: DataTableProps<T>) {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [ascending, setAscending] = useState(true);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle || !search) return rows;
    return rows.filter((row) => search(row).toLowerCase().includes(needle));
  }, [rows, query, search]);

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sortKey);
    if (!column?.sort) return filtered;
    // Copy first: sorting the array we were handed would mutate the caller's query cache.
    const out = [...filtered].sort(column.sort);
    return ascending ? out : out.reverse();
  }, [filtered, columns, sortKey, ascending]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, pageCount - 1);
  const visible = sorted.slice(current * pageSize, current * pageSize + pageSize);

  const selectedRows = rows.filter((r) => selected.has(rowKey(r)));
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(rowKey(r)));

  const toggleAll = () => {
    const next = new Set(selected);
    for (const row of visible) {
      if (allVisibleSelected) next.delete(rowKey(row));
      else next.add(rowKey(row));
    }
    setSelected(next);
  };

  const toggle = (row: T) => {
    const next = new Set(selected);
    const key = rowKey(row);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const sortBy = (column: Column<T>) => {
    if (!column.sort) return;
    if (sortKey === column.key) setAscending((a) => !a);
    else {
      setSortKey(column.key);
      setAscending(true);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {toolbar}

        {onDelete && (
          <Button
            variant="secondary"
            disabled={selectedRows.length === 0}
            onClick={() => {
              const label =
                deleteLabel?.(selectedRows) ??
                `Delete ${selectedRows.length} item(s)? This cannot be undone.`;
              if (confirm(label)) {
                onDelete(selectedRows);
                setSelected(new Set());
              }
            }}
          >
            <Trash2 size={14} />
            Delete
          </Button>
        )}

        {search && (
          <div className="relative ml-auto">
            <Search
              size={15}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="w-56 pl-8"
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-[--radius] border border-border bg-card">
        <table className="w-full min-w-[40rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {onDelete && (
                <th className="w-10 px-3 py-2.5">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all rows on this page"
                  />
                </th>
              )}

              {columns.map((column) => (
                <th
                  key={column.key}
                  style={column.width ? { width: column.width } : undefined}
                  className={cn(
                    'px-3 py-2.5 font-medium text-muted-foreground',
                    column.align === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  {column.sort ? (
                    <button
                      type="button"
                      onClick={() => sortBy(column)}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      {column.header}
                      {sortKey === column.key ? (
                        ascending ? (
                          <ChevronUp size={13} />
                        ) : (
                          <ChevronDown size={13} />
                        )
                      ) : (
                        <ChevronsUpDown size={13} className="opacity-40" />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}

              {(onEdit || onDelete) && (
                <th className="w-24 px-3 py-2.5 text-right font-medium text-muted-foreground">
                  Actions
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (onDelete ? 1 : 0) + (onEdit || onDelete ? 1 : 0)}
                  className="px-3 py-12 text-center text-muted-foreground"
                >
                  {query ? `Nothing matches "${query}".` : (empty ?? 'Nothing here yet.')}
                </td>
              </tr>
            ) : (
              visible.map((row) => (
                <tr
                  key={rowKey(row)}
                  className="border-b border-border last:border-0 hover:bg-muted/30"
                >
                  {onDelete && (
                    <td className="px-3 py-2.5">
                      <Checkbox
                        checked={selected.has(rowKey(row))}
                        onCheckedChange={() => toggle(row)}
                        aria-label="Select row"
                      />
                    </td>
                  )}

                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn('px-3 py-2.5', column.align === 'right' && 'text-right')}
                    >
                      {column.render(row)}
                    </td>
                  ))}

                  {(onEdit || onDelete) && (
                    <td className="px-3 py-2.5">
                      <div className="flex justify-end gap-1">
                        {onEdit && (
                          <button
                            type="button"
                            onClick={() => onEdit(row)}
                            className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label="Edit"
                            title="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                        )}
                        {onDelete && (
                          <button
                            type="button"
                            onClick={() => {
                              const label =
                                deleteLabel?.([row]) ?? 'Delete this item? This cannot be undone.';
                              if (confirm(label)) onDelete([row]);
                            }}
                            className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label="Delete"
                            title="Delete"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <span>
          Total: <span className="font-medium text-foreground tabular-nums">{sorted.length}</span>
          {selectedRows.length > 0 && ` · ${selectedRows.length} selected`}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <div className="w-32">
            <Select
              value={String(pageSize)}
              onChange={(value) => {
                setPageSize(Number(value));
                setPage(0);
              }}
              aria-label="Rows per page"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              className="h-8 px-2"
              disabled={current === 0}
              onClick={() => setPage(current - 1)}
            >
              Previous
            </Button>
            <span className="px-2 tabular-nums">
              {current + 1} / {pageCount}
            </span>
            <Button
              variant="ghost"
              className="h-8 px-2"
              disabled={current >= pageCount - 1}
              onClick={() => setPage(current + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
